import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { PitchLimitStatus } from '../services/api';

@customElement('pitch-limit-indicator')
export class PitchLimitIndicator extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  status: PitchLimitStatus | null = null;

  @property({ type: Boolean })
  compact = false;

  @property({ type: Boolean })
  showRestStatus = true;

  @state()
  private previousStatus: 'ok' | 'warning' | 'limit_reached' | null = null;

  @state()
  private previousCount: number | null = null;

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has('status') && this.status) {
      const prevStatus = this.previousStatus;
      const newStatus = this.status.status;

      // Check for status transitions and trigger haptic feedback
      if (prevStatus && prevStatus !== newStatus) {
        this.triggerHapticFeedback(newStatus);
      }

      // Check for rest threshold crossings
      if (this.previousCount !== null && this.status.rest_thresholds) {
        for (const t of this.status.rest_thresholds) {
          if (this.previousCount < t.pitch_count && this.status.current_count >= t.pitch_count) {
            // Crossed a rest threshold — haptic feedback
            if ('vibrate' in navigator) {
              navigator.vibrate([100, 50, 100]);
            }
            break;
          }
        }
      }

      this.previousStatus = newStatus;
      this.previousCount = this.status.current_count;
    }
  }

  private triggerHapticFeedback(newStatus: 'ok' | 'warning' | 'limit_reached') {
    // Check if vibration API is available
    if ('vibrate' in navigator) {
      switch (newStatus) {
        case 'warning':
          // Double short vibration for warning
          navigator.vibrate([50, 50, 50]);
          break;
        case 'limit_reached':
          // Long vibration for limit reached
          navigator.vibrate([200, 100, 200]);
          break;
        default:
          // No vibration for returning to ok
          break;
      }
    }
  }

  private getStatusColor(): string {
    if (!this.status) return 'bg-base-300';
    switch (this.status.status) {
      case 'limit_reached':
        return 'bg-error';
      case 'warning':
        return 'bg-warning';
      default:
        return 'bg-success';
    }
  }

  private getTextColor(): string {
    if (!this.status) return '';
    switch (this.status.status) {
      case 'limit_reached':
        return 'text-error';
      case 'warning':
        return 'text-warning';
      default:
        return 'text-success';
    }
  }

  render() {
    if (!this.status) {
      return html`<div class="skeleton h-4 w-20"></div>`;
    }

    const pct = Math.min(this.status.percentage, 100);

    if (this.compact) {
      return this.renderCompact(pct);
    }

    return this.renderFull(pct);
  }

  private renderCompact(pct: number) {
    return html`
      <div class="flex items-center gap-1">
        <div class="relative w-12 h-2 bg-base-300 rounded-full overflow-hidden">
          <div
            class="absolute left-0 top-0 h-full transition-all duration-300 ${this.getStatusColor()}"
            style="width: ${pct}%"
          ></div>
        </div>
        <span class="text-xs font-mono tabular-nums ${this.getTextColor()}">
          ${this.status!.current_count}
        </span>
      </div>
    `;
  }

  private renderFull(pct: number) {
    // Determine the current rest consequence (highest crossed threshold)
    const crossedThresholds = (this.status!.rest_thresholds || [])
      .filter(t => this.status!.current_count >= t.pitch_count);
    const currentRestDays = crossedThresholds.length > 0
      ? Math.max(...crossedThresholds.map(t => t.rest_days))
      : 0;

    return html`
      <div class="space-y-1">
        <div class="flex items-center gap-2">
          <!-- Progress bar -->
          <div class="relative flex-1 h-3 bg-base-300 rounded-full overflow-hidden">
            <div
              class="absolute left-0 top-0 h-full transition-all duration-300 ${this.getStatusColor()}"
              style="width: ${pct}%"
            ></div>
            <!-- Rest threshold markers -->
            ${(this.status!.rest_thresholds || []).map((t) => {
              const pos = (t.pitch_count / this.status!.max_pitches) * 100;
              if (pos > 100) return nothing;
              const color = t.rest_days >= 3 ? 'bg-error/60' : t.rest_days >= 2 ? 'bg-warning/60' : 'bg-info/60';
              return html`
                <div
                  class="absolute top-0 bottom-0 w-0.5 ${color}"
                  style="left: ${pos}%"
                  title="${t.pitch_count} pitches: ${t.label}"
                ></div>
              `;
            })}
            <!-- Warning threshold marker -->
            ${this.status!.warning_threshold > 0
              ? html`
                  <div
                    class="absolute top-0 bottom-0 w-0.5 bg-base-content/30"
                    style="left: ${(this.status!.warning_threshold / this.status!.max_pitches) * 100}%"
                  ></div>
                `
              : nothing}
          </div>

          <!-- Count text -->
          <span
            class="text-sm font-mono tabular-nums min-w-[4rem] text-right ${this.status!.status === 'limit_reached'
              ? 'text-error font-bold'
              : ''}"
          >
            ${this.status!.current_count}/${this.status!.max_pitches}
          </span>

          <!-- Status badge -->
          ${this.status!.status === 'limit_reached'
            ? html`<span class="badge badge-error badge-sm">LIMIT</span>`
            : this.status!.status === 'warning'
              ? html`<span class="badge badge-warning badge-sm">WARNING</span>`
              : nothing}
        </div>

        <!-- Age group and remaining info -->
        <div class="flex justify-between text-xs text-base-content/70">
          <span>${this.status!.age_group}</span>
          <span>${this.status!.remaining_pitches} remaining</span>
        </div>

        <!-- Next rest threshold info -->
        ${this.status!.next_rest_threshold ? html`
          <div class="text-xs text-base-content/60">
            ${this.status!.next_rest_threshold.pitch_count - this.status!.current_count} more pitches until ${this.status!.next_rest_threshold.rest_days} day${this.status!.next_rest_threshold.rest_days !== 1 ? 's' : ''} rest required
          </div>
        ` : nothing}

        <!-- Current rest consequence badge -->
        ${currentRestDays > 0 ? html`
          <div class="flex items-center gap-1 text-xs font-medium ${currentRestDays >= 3 ? 'text-error' : 'text-warning'}">
            <span>⚠</span>
            <span>${currentRestDays} day${currentRestDays !== 1 ? 's' : ''} rest after this game</span>
          </div>
        ` : nothing}

        <!-- Rest status if available and enabled -->
        ${this.showRestStatus && this.status!.rest_status
          ? this.renderRestStatus()
          : nothing}
      </div>
    `;
  }

  private renderRestStatus() {
    const rest = this.status!.rest_status!;
    const statusBadge =
      rest.status === 'available'
        ? html`<span class="badge badge-success badge-xs">Available</span>`
        : rest.status === 'resting'
          ? html`<span class="badge badge-warning badge-xs">Resting</span>`
          : html`<span class="badge badge-info badge-xs">Limited</span>`;

    return html`
      <div class="mt-2 p-2 bg-base-200 rounded-lg">
        <div class="flex items-center justify-between text-xs">
          <span class="font-medium">Rest Status</span>
          ${statusBadge}
        </div>
        ${rest.status === 'available'
          ? nothing
          : html`
              <div class="mt-1 text-xs text-base-content/70">
                ${rest.available_date ? html`
                  Available: ${new Date(rest.available_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  (${Math.max(0, Math.min(rest.rest_days_needed, rest.rest_days_needed - rest.rest_days_elapsed))} days)
                ` : nothing}
              </div>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pitch-limit-indicator': PitchLimitIndicator;
  }
}
